import { matchPath } from 'react-router-dom';

/**
 * Deny-by-default route allowlist for the scoped `raise_owner` persona.
 *
 * This is the SINGLE source of truth for which authenticated surfaces a
 * `raise_owner` may load. The Phase-1 wall was a deny-LIST (allow-by-default):
 * only a handful of guards (`ApprovalPendingRoute` / `AdminRoute` /
 * `SubscriptionRoute` / `HomeRedirect`) bounced an owner, so EVERY
 * `ProtectedRoute`-only or unguarded route (`/organization`, `/docs`,
 * `/intelligence-platform`, `/intelligence/investor`, `/pending-approval`, …)
 * leaked to an owner. Inverting it to an allow-LIST (deny-by-default) enforced at
 * the layout split closes that whole class of leak in one place — both guarded
 * and unguarded routes — and any future route is denied to owners until it is
 * deliberately added here.
 *
 * Routes an owner may load:
 *  - `/owner`, `/owner/billing`, and `/owner/claims/verify/:token` — the owner
 *    console, the billing/subscription page, and the claim-verify landing (the
 *    first-class owner surfaces).
 *  - `/profile` — the account page (owner-scoped to password/MFA/email; see
 *    `UserProfile`).
 *  - the public Raises directory & profile routes — owners may browse/view these
 *    the same way an anonymous visitor can; the tiered `/api/raises` +
 *    `/api/companies` reads are the server-side boundary.
 *  - `/terms` and `/privacy` — the public legal pages. These are plain public
 *    routes (no guard), but they are NOT public-ONLY routes, so an authenticated
 *    owner resolves to the OwnerLayout and would otherwise be walled off. The
 *    owner-scoped `/profile` Terms tab explicitly links here ("View Terms →" /
 *    "View Policy →") and the ToS-acknowledgment copy tells the owner they
 *    accepted these agreements, so blocking them is a compliance defect. They
 *    expose no developer/admin/investor functionality, so they are safe to allow.
 *
 * Anything NOT matched here is denied to an owner and redirected to `/owner` by
 * the layout-level wall in `App.tsx`. Patterns use react-router `matchPath`, so
 * dynamic segments (`:slug`, `:token`) match a single path segment and a sibling
 * like `/raisesX` does NOT match `/raises`.
 */
export const OWNER_ALLOWED_ROUTES = [
  '/owner',
  '/owner/billing',
  '/owner/claims/verify/:token',
  '/profile',
  '/raises',
  '/raises/directory',
  '/raises/:slug',
  '/raises/:slug/claim',
  '/companies/:slug',
  '/terms',
  '/privacy',
] as const;

/**
 * Whether `pathname` is a route a scoped `raise_owner` is allowed to load.
 *
 * `matchPath` is end-anchored by default, so each pattern matches the full
 * pathname — `/owner` does not match `/owner/anything`, and `:slug` matches
 * exactly one segment. This keeps the wall deny-by-default: a path that matches
 * none of the allow patterns returns `false`.
 *
 * @param pathname the current `location.pathname` (no query string / hash).
 */
export function isOwnerAllowedPath(pathname: string): boolean {
  return OWNER_ALLOWED_ROUTES.some((pattern) => matchPath(pattern, pathname) !== null);
}
